import t from "../../../../../../i18n";
import type { RowValue } from "../../../../../../lib/csv";
import { FILE_PARSING_ERROR } from "../../../../../../domain/parser/common/file";
import type { ParsingError, ParsingErrorMessageMap } from "../../../../../../domain/parser/types";

export type FileKeys = "failures" | "impacts" | "nonExpected";

export enum FAILURE_PARSING_ERROR {
  MISSING_ALL_COLUMNS = "MISSING_ALL_COLUMNS" /* "All columns are missing", */,
  MISSING_COLUMNS = "MISSING_COLUMNS" /* "Missing columns", */,
  UNKNOWN_QUANTITY = "UNKNOWN_QUANTITY" /* "Unknown quantity", */,
  TECHNICAL_NAME_TOO_LONG = "TECHNICAL_NAME_TOO_LONG" /* "Technical name is too long", */,
  DISPLAY_NAME_TOO_LONG = "DISPLAY_NAME_TOO_LONG" /* "Display name is too long", */,
  DUPLICATED_TECHNICAL_NAME = "DUPLICATED_TECHNICAL_NAME" /* "Duplicated technical name", */,
  DUPLICATED_DISPLAY_NAME = "DUPLICATED_DISPLAY_NAME" /* "Duplicated display name", */,
  TECHNICAL_NAME_EMPTY = "TECHNICAL_NAME_EMPTY",
  DISPLAY_NAME_EMPTY = "DISPLAY_NAME_EMPTY",
  DISPLAY_NAME_COLUMN_FORBIDDEN_WHEN_DISABLED = "DISPLAY_NAME_COLUMN_FORBIDDEN_WHEN_DISABLED",
  IMPACT_VALUE_NOT_A_NUMBER = "IMPACT_VALUE_NOT_A_NUMBER" /* "Impact value is not a number", */,
  MIN_GREATER_THAN_MAX = "MIN_GREATER_THAN_MAX" /* "Min is greater than max", */,
  MAGNITUDE_NOT_NUMBER = "MAGNITUDE_NOT_NUMBER" /* "Magnitude must be a positive number", */,
  MAX_NOT_A_NUMBER = "MAX_NOT_A_NUMBER" /* "Max must be a positive number", */,
  MIN_NOT_A_NUMBER = "MIN_NOT_A_NUMBER" /* "Min must be a positive number", */,
  ACTIVATED_INVALID = "ACTIVATED_INVALID" /* "Activated must be a boolean", */,
  NAME_NOT_HUMAN_READABLE_ID = "NAME_NOT_HUMAN_READABLE_ID" /* "Name must be human readable", */,
  WRONG_INPUT_MULTI_VALUE = "WRONG_INPUT_MULTI_VALUE" /* "Wrong input format for multi-value variables", */,
  WRONG_INPUT_REPARABILITY = "WRONG_INPUT_REPARABILITY" /* "Reparability must be REPARABILITY_0, REPARABILITY_1 or REPARABILITY_2", */,
  MISSING_SYSTEM_CATEGORIES = "MISSING_SYSTEM_CATEGORIES" /* "Need permission to create a category for the system column", */,
  MISSING_EQUIPMENT_TYPE_CATEGORIES = "MISSING_EQUIPMENT_TYPE_CATEGORIES" /* "Need permission to create a category for the equipmentType column", */,
  MISSING_DEGRADATION_TYPE_CATEGORIES = "MISSING_DEGRADATION_TYPE_CATEGORIES" /* "Need permission to create a category for the degradationType column", */,
  MISSING_FAILURE_CATEGORIES = "MISSING_FAILURE_CATEGORIES" /* "Need permission to create a category for the failure column", */,
  MISSING_EQUIPMENTS_CATEGORIES = "MISSING_EQUIPMENTS_CATEGORIES" /* "Need permission to create a category for the equipments column", */,
  MISSING_SUSPECTED_ELEMENTS_CATEGORIES = "MISSING_SUSPECTED_ELEMENTS_CATEGORIES" /* "Need permission to create a category for the suspectedElements column", */,
  FILTER_FORMULA_UNKNOWN_QUANTITY = "FILTER_FORMULA_UNKNOWN_QUANTITY" /* "Unknown quantity in FilterFormula", */,
  WRONG_FORMAT_FILTER_FORMULA = "WRONG_FORMAT_FILTER_FORMULA" /* "Wrong value format for FilterFormula", */,
  MISSING_FILTER_MIN_DEPENDENCIES = "MISSING_FILTER_MIN_DEPENDENCIES" /* "Definition of a filterMin involving a filterFormula", */,
  MISSING_FILTER_MAX_DEPENDENCIES = "MISSING_FILTER_MAX_DEPENDENCIES" /* "Definition of a filterMax involving a filterFormula", */,
  FILTER_MIN_MUST_BE_LESS_THAN_FILTER_MAX = "FILTER_MIN_MUST_BE_LESS_THAN_FILTER_MAX" /* "FilterMin must be less than FilterMax", */,
  FILTER_MIN_MAX_MUST_BE_NUMBER = "FILTER_MIN_MAX_MUST_BE_NUMBER" /* "FilterMin and FilterMax must be numeric types", */,
  MISSING_FILTER_FOMULA_DEPENDENCIED = "MISSING_FILTER_FOMULA_DEPENDENCIED" /* "To define filterformula you must at least define filterMin or filterMax", */,
  WRONG_DUPLICATION_FILTER_FOMULA = "WRONG_DUPLICATION_FILTER_FOMULA" /* "It is not possible to declare two FilterFormula filters for the same failure quantity", */,
  NAME_TOO_LONG = "NAME_TOO_LONG" /* "Name is too long", */,
  UNIT_TOO_LONG = "UNIT_TOO_LONG" /* "Unit is too long", */,
  INVALID_FILTER_TYPE = "INVALID_FILTER_TYPE" /* "Invalid value for the filterType column", */,
}

export const FAILURE_PARSING_ERROR_MESSAGES: ParsingErrorMessageMap<
  FAILURE_PARSING_ERROR | FILE_PARSING_ERROR
> = {
  [FILE_PARSING_ERROR.FILE_EMPTY]: () =>
    t("setup.diagnosis.observable.import_observables.error.empty_file"),
  [FAILURE_PARSING_ERROR.TECHNICAL_NAME_TOO_LONG]: data =>
    t("setup.diagnosis.faults.modal.error.technical_name_too_long", {
      nameFaults: data.value?.rowNames ?? [],
    }),
  [FAILURE_PARSING_ERROR.DUPLICATED_TECHNICAL_NAME]: data =>
    t("setup.diagnosis.failures.modal.error.duplicated_technical_name", {
      nameFaults: data.value?.rowNames ?? [],
    }),
  [FAILURE_PARSING_ERROR.DUPLICATED_DISPLAY_NAME]: data =>
    t("setup.diagnosis.faults.modal.error.duplicated_display_name", {
      technicalNames: data.value?.rowNames ?? [],
    }),
  [FAILURE_PARSING_ERROR.TECHNICAL_NAME_EMPTY]: data =>
    t("setup.diagnosis.faults.modal.error.technical_name_empty", {
      lines: data.value?.lines ?? [],
    }),
  [FAILURE_PARSING_ERROR.DISPLAY_NAME_EMPTY]: data =>
    t("setup.diagnosis.faults.modal.error.display_name_empty", {
      nameFaults: data.value?.rowNames ?? [],
    }),
  [FAILURE_PARSING_ERROR.DISPLAY_NAME_COLUMN_FORBIDDEN_WHEN_DISABLED]: () =>
    t("setup.diagnosis.failures.modal.permissionRequired.notAllow", {
      infos: ["displayName"],
    }),
  [FAILURE_PARSING_ERROR.WRONG_FORMAT_FILTER_FORMULA]: data =>
    t("setup.diagnosis.failures.modal.error.wrong_format_filterFormula", {
      nameFailures: data.value?.rowNames ?? [],
    }),
  [FAILURE_PARSING_ERROR.WRONG_DUPLICATION_FILTER_FOMULA]: data =>
    t("setup.diagnosis.failures.modal.error.wrong_duplication_filterFomula", {
      nameFailures: data.value?.rowNames ?? [],
    }),
  [FAILURE_PARSING_ERROR.FILTER_FORMULA_UNKNOWN_QUANTITY]: data =>
    t("setup.diagnosis.failures.modal.error.filter_formula_unknown_quantity", {
      nameFailures: data.value ?? [],
    }),
  [FAILURE_PARSING_ERROR.FILTER_MIN_MAX_MUST_BE_NUMBER]: data =>
    t("setup.diagnosis.failures.modal.error.filterMinMax_must_be_number", {
      nameFailures: data.value?.rowNames ?? [],
    }),
  [FAILURE_PARSING_ERROR.FILTER_MIN_MUST_BE_LESS_THAN_FILTER_MAX]: data =>
    t("setup.diagnosis.failures.modal.error.filterMin_must_be_less_than_FilterMax", {
      nameFailures: data.value?.rowNames ?? [],
    }),
  [FAILURE_PARSING_ERROR.MISSING_FILTER_MIN_DEPENDENCIES]: data =>
    t("setup.diagnosis.failures.modal.error.wrong_format_filterMin", {
      nameFailures: data.value?.rowNames ?? [],
    }),
  [FAILURE_PARSING_ERROR.MISSING_FILTER_MAX_DEPENDENCIES]: data =>
    t("setup.diagnosis.failures.modal.error.wrong_format_filterMax", {
      nameFailures: data.value?.rowNames ?? [],
    }),
  [FAILURE_PARSING_ERROR.MISSING_FILTER_FOMULA_DEPENDENCIED]: data =>
    t("setup.diagnosis.failures.modal.error.missing_filterFomula_dependencied", {
      nameFailures: data.value?.rowNames ?? [],
    }),
  [FAILURE_PARSING_ERROR.UNKNOWN_QUANTITY]: data =>
    t("setup.diagnosis.failures.modal.error.unknown_quantity", {
      failureNames: data.value ?? [],
    }),
  [FAILURE_PARSING_ERROR.MISSING_ALL_COLUMNS]: data =>
    t("setup.diagnosis.failures.modal.error.missing_column", {
      properties: data.value?.data ?? [],
    }),
  [FAILURE_PARSING_ERROR.MISSING_COLUMNS]: data =>
    t("setup.diagnosis.failures.modal.error.missingMandatoryColumns", {
      properties: data.value ?? [],
    }),
  [FAILURE_PARSING_ERROR.ACTIVATED_INVALID]: data =>
    t("setup.diagnosis.failures.modal.error.activated_invalid", {
      nameFailures: data.value?.rowNames ?? [],
    }),
  [FAILURE_PARSING_ERROR.IMPACT_VALUE_NOT_A_NUMBER]: data =>
    t("setup.diagnosis.failures.modal.error.bad_impacts", {
      failure: data.value?.[0][0] ?? "<unknown failure>",
      quantity: data.value?.[0][1] ?? "<unknown quantity>",
    }),
  [FAILURE_PARSING_ERROR.MAGNITUDE_NOT_NUMBER]: data =>
    t("setup.diagnosis.failures.modal.error.bad_magnitude", {
      nameFailures: data.value?.rowNames ?? ["<unknown failure>"],
    }),
  [FAILURE_PARSING_ERROR.MIN_GREATER_THAN_MAX]: data =>
    t("setup.diagnosis.failures.modal.error.min_greater_than_max", {
      nameFailures: data.value?.rowNames ?? ["<unknown failure>"],
    }),
  [FAILURE_PARSING_ERROR.MAX_NOT_A_NUMBER]: data =>
    t("setup.diagnosis.failures.modal.error.max_not_a_number", {
      nameFailures: data.value?.rowNames ?? ["<unknown failure>"],
    }),
  [FAILURE_PARSING_ERROR.MIN_NOT_A_NUMBER]: data =>
    t("setup.diagnosis.failures.modal.error.min_not_a_number", {
      nameFailures: data.value?.rowNames ?? ["<unknown failure>"],
    }),
  [FAILURE_PARSING_ERROR.NAME_NOT_HUMAN_READABLE_ID]: data =>
    t("setup.diagnosis.failures.modal.error.name_not_human_readable", {
      nameFailures: data.value?.rowNames ?? ["<unknown failure>"],
    }),
  [FAILURE_PARSING_ERROR.WRONG_INPUT_MULTI_VALUE]: data =>
    t("setup.diagnosis.failures.modal.error.wrongInputMultiValue", {
      nameFailures: data.value?.rowNames ?? ["<unknown failure>"],
    }),
  [FAILURE_PARSING_ERROR.WRONG_INPUT_REPARABILITY]: data =>
    t("setup.diagnosis.failures.modal.error.wrongInputReparability", {
      nameFailures: data.value?.rowNames ?? ["<unknown failure>"],
    }),
  [FAILURE_PARSING_ERROR.MISSING_SYSTEM_CATEGORIES]: data =>
    t("setup.diagnosis.failures.modal.permissionRequired.notAllow", {
      infos: data.value.column ?? "<unknown column>",
    }),
  [FAILURE_PARSING_ERROR.MISSING_EQUIPMENT_TYPE_CATEGORIES]: data =>
    t("setup.diagnosis.failures.modal.permissionRequired.notAllow", {
      infos: data.value.column ?? "<unknown column>",
    }),
  [FAILURE_PARSING_ERROR.MISSING_DEGRADATION_TYPE_CATEGORIES]: data =>
    t("setup.diagnosis.failures.modal.permissionRequired.notAllow", {
      infos: data.value.column ?? "<unknown column>",
    }),
  [FAILURE_PARSING_ERROR.MISSING_FAILURE_CATEGORIES]: data =>
    t("setup.diagnosis.failures.modal.permissionRequired.notAllow", {
      infos: data.value.column ?? "<unknown column>",
    }),
  [FAILURE_PARSING_ERROR.MISSING_EQUIPMENTS_CATEGORIES]: data =>
    t("setup.diagnosis.failures.modal.permissionRequired.notAllow", {
      infos: data.value.column ?? "<unknown column>",
    }),
  [FAILURE_PARSING_ERROR.MISSING_SUSPECTED_ELEMENTS_CATEGORIES]: data =>
    t("setup.diagnosis.failures.modal.permissionRequired.notAllow", {
      infos: data.value.column ?? "<unknown column>",
    }),
  [FAILURE_PARSING_ERROR.NAME_TOO_LONG]: data =>
    t("setup.diagnosis.failures.modal.error.name_too_long", {
      nameFailures: data.value?.rowNames ?? "<unknown column>",
    }),
  [FAILURE_PARSING_ERROR.UNIT_TOO_LONG]: data =>
    t("setup.diagnosis.failures.modal.error.unit_too_long", {
      nameFailures: data.value?.rowNames ?? "<unknown column>",
    }),
  [FAILURE_PARSING_ERROR.INVALID_FILTER_TYPE]: data =>
    t("setup.diagnosis.failures.modal.error.invalid_filter_type", {
      failure: data.value?.failures?.join(", ") ?? "<unknown failure>",
      allowed: data.value?.allowed,
    }),
};

export const getFailureParsingErrorMessage = (
  err: ParsingError<FAILURE_PARSING_ERROR | FILE_PARSING_ERROR>,
) => FAILURE_PARSING_ERROR_MESSAGES[err.kind]?.(err.data) ?? "Unknown error";

export const expectedFormats = ["csv"];
export const expectedColumns = [
  "name",
  "technicalName",
  "displayName",
  "activated",
  "description",
  "unit",
  "minimumValue",
  "maximumValue",
  "magnitude",
  "degradationType",
  "failureCategory",
  "system",
  "equipments",
  "equipmentType",
  "suspectedElements",
  "reparability",
  "filterFormula*",
  "filterMin*",
  "filterMax*",
  "filterType*",
] as const;
export type FixedColumn = (typeof expectedColumns)[number];

const COMMON_MANDATORY_COLUMNS: FixedColumn[] = [
  "activated",
  "minimumValue",
  "maximumValue",
  "magnitude",
];

export function getMandatoryColumnsMap(): Map<FixedColumn, string[]> {
  const mandatory = new Map<FixedColumn, string[]>();

  for (const column of COMMON_MANDATORY_COLUMNS) {
    mandatory.set(column, [column]);
  }

  mandatory.set("technicalName", ["technicalName"]);
  mandatory.set("displayName", ["displayName"]);

  return mandatory;
}

export type Categories =
  | "system"
  | "equipmentType"
  | "degradationType"
  | "failureCategory"
  | "equipments"
  | "suspectedElements";

export type ErrorType = "info" | "error";

export interface FailuresFile {
  file: File;
  fileName: string;
  content: RowValue[] | undefined;
  toCreate: { column: Categories; values: string[] }[];
}
